import React, { useState } from "react";
import { Modal, Table, Checkbox, InputNumber, Input, message } from "antd";
import { addPharmacyReturnApi } from "../../../services/pharmacyReturns.services";
import { getErrorMessage } from "../../../helpers/error";
import { formatAmount } from "../common/money";

const RaiseReturnModal = ({ open, setOpen, bill, refreshPage, onCreated }) => {
  const [selected, setSelected] = useState({});
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const items = bill?.items || [];

  /*
   * What may still go back on this line.
   *
   * The quantity that was billed, less anything already returned against it
   * where the bill reports that - two returns of one each against a line
   * billed one is the same mistake as a single return of two.
   */
  const returnableQty = (item) => {
    const billed = Number(item?.quantity) || 0;
    const already = Number(item?.returned_qty ?? item?.return_qty ?? 0) || 0;
    return Math.max(billed - already, 0);
  };

  const nameOf = (item) =>
    item?.pharmacy_medicine_name || item?.medicine_name || "This medicine";

  const toggleItem = (item, checked) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (checked) {
        const max = returnableQty(item);
        next[item.id] = { qty: max, max };
      } else {
        delete next[item.id];
      }
      return next;
    });
  };

  /*
   * The cap, held here rather than left to the control.
   *
   * antd's `max` governs the steppers and a blur, not a figure typed straight
   * in - `onChange` fires with whatever was keyed, and the box only colours
   * itself out of range. So a line billed one went back ten: the state took
   * the ten and Submit posted it. The quantity is clamped on its way into
   * state instead, and the pharmacist is told why it stopped where it did.
   */
  const sayTheCap = (item, max) =>
    message.warning({
      key: `return-cap-${item.id}`,
      content:
        max === 0
          ? `${nameOf(item)} has already been returned in full.`
          : `Only ${max} of ${nameOf(item)} ${max === 1 ? "is" : "are"} left to return.`,
    });

  const setQty = (item, qty) => {
    const max = returnableQty(item);
    const asked = Number(qty) || 0;
    if (asked > max) sayTheCap(item, max);
    setSelected((prev) => ({
      ...prev,
      [item.id]: { ...prev[item.id], qty: Math.min(Math.max(asked, 0), max), max },
    }));
  };

  /*
   * Refusing the keystroke, rather than correcting it after the fact.
   *
   * Clamping on the way into state is not enough on its own: the box is
   * controlled, so when 10 is clamped back to the 1 it already held, React
   * sets no new value and antd leaves the typed "10" sitting in the field
   * until it loses focus. It reads as accepted. So a digit that would carry
   * the figure past what is left to return never reaches the box at all - and
   * says why, rather than appearing to be a dead key.
   */
  const wouldExceed = (element, insert, max) => {
    const start = element.selectionStart ?? element.value.length;
    const end = element.selectionEnd ?? start;
    const next = element.value.slice(0, start) + insert + element.value.slice(end);
    return Number(next) > max;
  };

  const capKeystrokes = (item) => {
    const max = returnableQty(item);
    return {
      onKeyDown: (event) => {
        /* Shortcuts, arrows, Backspace, Tab - anything that is not a digit
           being added - are the control's own business. */
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        if (!/^[0-9]$/.test(event.key)) return;
        if (wouldExceed(event.currentTarget, event.key, max)) {
          event.preventDefault();
          sayTheCap(item, max);
        }
      },
      onPaste: (event) => {
        const pasted = event.clipboardData?.getData("text") ?? "";
        if (wouldExceed(event.currentTarget, pasted, max)) {
          event.preventDefault();
          sayTheCap(item, max);
        }
      },
    };
  };

  const closeAndReset = () => {
    setSelected({});
    setReason("");
    setOpen(false);
  };

  const handleSubmit = () => {
    /* Clamped once more on the way out: the cap is the thing being enforced,
       and enforcing it in one place only is how it came to be missed. */
    const lines = Object.entries(selected)
      .map(([billItemId, val]) => {
        const item = items.find((row) => String(row.id) === String(billItemId));
        const max = item ? returnableQty(item) : 0;
        return {
          bill_item: Number(billItemId),
          quantity: Math.min(Math.max(Number(val.qty) || 0, 0), max),
          reason,
        };
      })
      .filter((line) => line.quantity > 0);

    if (!lines.length) {
      message.warning("Select at least one item to return");
      return;
    }

    setLoading(true);
    addPharmacyReturnApi({ bill: bill.id, remarks: reason, items: lines })
      .then((res) => {
        message.success(`Return ${res.data.return_number} raised`);
        setLoading(false);
        closeAndReset();
        refreshPage();
        onCreated?.(res.data);
      })
      .catch((err) => {
        /*
         * What the server actually said, where it said anything.
         *
         * Only `data.message` was read before, so a refusal that arrived in
         * any other shape - a serializer's own `{"items": [...]}`, a
         * `detail`, a bare list - was reported as "Failed to raise return",
         * which tells the pharmacist nothing about which line was wrong or
         * why. The dialog stays open on a refusal so the answer can be read
         * against the figures that caused it.
         */
        message.error({
          content: getErrorMessage(err),
          /* Long enough to read a sentence naming a medicine and a quantity. */
          duration: 6,
        });
        setLoading(false);
      });
  };

  const columns = [
    {
      title: "",
      key: "select",
      width: 40,
      render: (_, item) => (
        <Checkbox
          checked={Boolean(selected[item.id])}
          /* A line with nothing left to give back cannot be picked at all -
             a box that can only be ticked and then refused is worse than one
             that is not offered. */
          disabled={returnableQty(item) === 0}
          title={
            returnableQty(item) === 0
              ? "This line has already been returned in full"
              : undefined
          }
          onChange={(e) => toggleItem(item, e.target.checked)}
        />
      ),
    },
    {
      title: "Medicine",
      key: "medicine",
      render: (_, item) => item.pharmacy_medicine_name || item.medicine_name || "-",
    },
    {
      title: "Billed Qty",
      key: "quantity",
      /* What is left to return is said beside what was billed wherever the two
         differ, so the cap on the box next to it is not a surprise. */
      render: (_, item) => {
        const left = returnableQty(item);
        const billed = Number(item.quantity) || 0;
        return left === billed ? (
          billed
        ) : (
          <>
            {billed}
            <span style={{ color: "#667085", marginLeft: 6, fontSize: 12 }}>
              {left === 0 ? "all returned" : `${left} left`}
            </span>
          </>
        );
      },
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      render: (v) => formatAmount(v),
    },
    {
      title: "Return Qty",
      key: "return_qty",
      render: (_, item) =>
        selected[item.id] ? (
          <InputNumber
            min={0}
            max={returnableQty(item)}
            step={1}
            value={selected[item.id].qty}
            {...capKeystrokes(item)}
            onChange={(value) => setQty(item, value)}
          />
        ) : (
          "-"
        ),
    },
  ];

  return (
    <Modal
      title="Raise Return"
      open={open}
      onCancel={closeAndReset}
      centered
      width={760}
      okText="Submit Return"
      confirmLoading={loading}
      onOk={handleSubmit}
    >
      <Table
        dataSource={items}
        columns={columns}
        rowKey="id"
        pagination={false}
        scroll={{ x: "max-content" }}
      />
      <Input.TextArea
        style={{ marginTop: 16 }}
        rows={2}
        placeholder="Reason for return (optional)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
    </Modal>
  );
};

export default RaiseReturnModal;
